import type { QueryClient } from '@tanstack/react-query';

import { REALTIME_RECONNECTED, REALTIME_TOPIC_REVOKED } from '@/shared/realtime';
import type { ModuleSetupContext } from '@/shared/registry/types';

import { invalidateTask, invalidateTaskViews, taskKeys } from './api';

interface TaskEventPayload {
  taskId: string;
  groupId?: string | null;
  actorId?: string | null;
  reason?: string;
}

interface CommentEventPayload {
  taskId: string;
  commentId: string;
  authorId: string;
}

/**
 * Realtime events of the tasks module (docs/modules/tasks.md): participants receive `tasks.task-*` on their own
 * connections; open cards subscribe `task:<id>`, project views `task-group:<groupId>`. Every event refreshes what
 * shows the task; comment events refresh the open thread.
 */
export function setupTaskRealtime({ queryClient, realtime }: ModuleSetupContext): void {
  const onTask = (payload: TaskEventPayload) => {
    if (!payload.taskId) return;
    invalidateTask(queryClient, payload.taskId);
    if (payload.reason === 'comment')
      void queryClient.invalidateQueries({ queryKey: taskKeys.comments(payload.taskId) });
  };
  realtime.on<TaskEventPayload>('tasks.task-created', onTask);
  realtime.on<TaskEventPayload>('tasks.task-updated', onTask);
  realtime.on<TaskEventPayload>('tasks.task-deleted', (payload) => {
    if (!payload.taskId) return;
    void queryClient.invalidateQueries({ queryKey: taskKeys.detail(payload.taskId) });
    invalidateTaskViews(queryClient);
  });

  const onComment = (payload: CommentEventPayload) => {
    if (!payload.taskId) return;
    void queryClient.invalidateQueries({ queryKey: taskKeys.comments(payload.taskId) });
    void queryClient.invalidateQueries({ queryKey: taskKeys.detail(payload.taskId) });
  };
  realtime.on<CommentEventPayload>('tasks.comment-created', onComment);
  realtime.on<CommentEventPayload>('tasks.comment-updated', onComment);
  realtime.on<CommentEventPayload>('tasks.comment-deleted', onComment);

  realtime.on<TaskEventPayload>('tasks.timer-changed', (payload) => {
    void queryClient.invalidateQueries({ queryKey: taskKeys.timer });
    if (payload.taskId) void queryClient.invalidateQueries({ queryKey: taskKeys.time(payload.taskId) });
  });
  realtime.on('tasks.plan-changed', () => void queryClient.invalidateQueries({ queryKey: taskKeys.plan }));
  // A recurring task of the user changed (a task was made, a problem found, another tab paused it): lists and cards that show it.
  realtime.on('tasks.recurrence-changed', () => {
    void queryClient.invalidateQueries({ queryKey: taskKeys.recurrences });
    void queryClient.invalidateQueries({ queryKey: taskKeys.details });
  });
  realtime.on('tasks.task-viewed', () => void queryClient.invalidateQueries({ queryKey: taskKeys.counters }));
  realtime.on<{ topic?: string } | null>(REALTIME_TOPIC_REVOKED, (payload) =>
    onTopicRevoked(queryClient, payload?.topic),
  );
  realtime.on(REALTIME_RECONNECTED, () => void queryClient.invalidateQueries({ queryKey: taskKeys.all }));
}

/**
 * The user lost access to a task (`task:<id>`) or a project's tasks (`task-group:<id>`), e.g. was removed from the
 * group: an open card refetches into its "not found" state, the group's views and access (the "New task" button)
 * are asked again.
 */
export function onTopicRevoked(queryClient: QueryClient, topic: string | undefined): void {
  if (topic?.startsWith('task:')) {
    const taskId = topic.slice('task:'.length);
    invalidateTask(queryClient, taskId);
    void queryClient.invalidateQueries({ queryKey: taskKeys.comments(taskId) });
  } else if (topic?.startsWith('task-group:')) {
    const groupId = topic.slice('task-group:'.length);
    void queryClient.invalidateQueries({ queryKey: taskKeys.groupAccess(groupId) });
    void queryClient.invalidateQueries({ queryKey: taskKeys.gantt(groupId) });
    invalidateTaskViews(queryClient);
  }
}
